import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import useTestResults from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/hooks/useTestResults";
import {
  ResultsFormWrapper,
  ResultsTable,
  ResultsTableHeader,
  ResultsTableRow,
} from "@src/modules/shared/components/Results/ResultsForm.components";
import { StudyTestResult, TestStatus } from "@src/modules/shared/types";
import CustomResultRow from "@src/modules/shared/components/Results/ResultRow/CustomResultRow";
import Button from "@src/modules/shared/components/Button";
import { ReactComponent as PlusSign } from "@src/modules/shared/icons/plus.svg";
import { FieldArray, FormikProps } from "formik";
import { EditCustomResultsForm } from "@src/modules/shared/components/Results/CustomResults";

interface Props extends FormikProps<EditCustomResultsForm> {
  isEdit: boolean;
  selectedStudyId: string;
  selectedTest: StudyTestResult;
}

const CustomResultsForm: FunctionComponent<Props> = ({
  selectedStudyId,
  selectedTest,
  isEdit,
  values,
}) => {
  const { t } = useTranslation();
  const { data: testResultsData } = useTestResults(
    selectedStudyId,
    selectedTest.id,
  );
  const hasResults =
    testResultsData && testResultsData.resultStatus !== TestStatus.ActionNeeded;
  if (!hasResults) isEdit = true;

  return (
    <FieldArray name="results">
      {(arrayHelpers) => (
        <ResultsFormWrapper>
          <ResultsTable>
            <thead>
              <ResultsTableRow>
                <ResultsTableHeader>{t("Name")}</ResultsTableHeader>
                <ResultsTableHeader>{t("Results")}</ResultsTableHeader>
                <ResultsTableHeader>{t("Passed")}</ResultsTableHeader>
              </ResultsTableRow>
            </thead>
            <tbody>
              {values.results.map((result, index) => (
                <CustomResultRow
                  key={index}
                  result={result}
                  isEdit={isEdit}
                  resultIndex={index}
                  handleDeleteRow={() => arrayHelpers.remove(index)}
                />
              ))}
            </tbody>
          </ResultsTable>
          {isEdit && (
            <Button
              buttonType="tertiary"
              onClick={() => {
                if (values.results.length > 100) return;
                arrayHelpers.push({ label: "", value: "", isPassed: false });
              }}
            >
              <PlusSign /> {t("Add row")}
            </Button>
          )}
        </ResultsFormWrapper>
      )}
    </FieldArray>
  );
};

export default CustomResultsForm;
